import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Grid, Typography } from '@material-ui/core';
import { RootState } from 'redux/store';
import {
  fetchMindgageMetrics,
  startMindgageAssessment
} from 'redux/slices/assessment';
import CandidateCardContainer from 'components/containers/CandidateCardContainer';
import CultureProfileCharts from 'components/CultureProfileCharts';
import useLocales from 'hooks/useLocales';
import { useSnackbar } from 'notistack';
import EmptyContent from 'components/EmptyContent';
import useAuth from '@hooks/useAuth';
import SvgIconStyle from 'components/SvgIconStyle';

interface CandidateMindgageCardProps {
  title: string;
  candidate: any;
  mindgage: any;
  iconSrc?: string;
}

export const CandidateMindgageCard = ({
  title,
  candidate,
  mindgage,
  iconSrc
}: CandidateMindgageCardProps): JSX.Element => {
  const { user, isCandidate } = useAuth();
  const dispatch = useDispatch();
  const { t, currentLang } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  const [mindgageScoresTooltipIsOpen, setMindgageScoresTooltipIsOpen] =
    useState(false);
  const {
    initiatingMindgage,
    error: assessmentError,
    mindgageMetrics,
    loading: loadingMetrics
  } = useSelector((state: RootState) => state.assessment);

  // Get reports from mindgage data
  // Reports structure: mindgage.mainInfo.reports.Feedback and mindgage.mainInfo.reports['User Summary']
  const reports =
    mindgage?.mainInfo?.reports ||
    mindgage?.reports ||
    candidate?.mainInfo?.mindgage?.mainInfo?.reports;
  const feedbackReport = reports?.Feedback;
  const userSummaryReport = reports?.['User Summary'];

  // Recruiters can see both reports, candidates only see Feedback
  const isRecruiter = !isCandidate;

  const shouldShowButton =
    isCandidate &&
    user.candidate_id === candidate.id &&
    (!mindgage || !mindgage.test_logon_url || mindgage.result === null);

  const hasResult = mindgage?.result !== null && mindgage?.result !== undefined;

  // Result is an array of {metric_id, score} objects
  const resultData = mindgage?.result;
  const isResultArray = Array.isArray(resultData) && resultData.length > 0;

  // Always fetch metrics when result exists (we need metric definitions to map scores)
  useEffect(() => {
    if (
      hasResult &&
      isResultArray &&
      !mindgageMetrics?.length &&
      !loadingMetrics
    ) {
      dispatch(fetchMindgageMetrics(currentLang?.value || 'en') as any);
    }
  }, [
    hasResult,
    isResultArray,
    mindgageMetrics,
    loadingMetrics,
    currentLang,
    dispatch
  ]);

  const handleTakeTest = async () => {
    try {
      const response = await dispatch(startMindgageAssessment() as any);
      if (response && response.test_logon_url) {
        window.open(response.test_logon_url, '_blank');
      } else {
        enqueueSnackbar(t('Failed to initiate Mindgage assessment'), {
          variant: 'error'
        });
      }
    } catch (error: any) {
      enqueueSnackbar(
        error?.message || t('Failed to initiate Mindgage assessment'),
        { variant: 'error' }
      );
    }
  };

  useEffect(() => {
    if (assessmentError) {
      enqueueSnackbar(assessmentError, { variant: 'error' });
    }
  }, [assessmentError, enqueueSnackbar]);

  const handleDownloadReport = (reportUrl: string, reportName: string) => {
    if (!reportUrl) {
      enqueueSnackbar(t('Report not available'), { variant: 'error' });
      return;
    }

    // Check if it's an assessment report URL that matches our pattern
    const isPdfReportUrl = (url: string): boolean =>
      /\/api\/assessment\/report\/(\d+)\/([^/]+)\/?$/.test(url);

    if (isPdfReportUrl(reportUrl)) {
      // Use the full URL directly to preserve special characters
      // This avoids issues with URL reconstruction that can corrupt the PDF
      import('../../../utils/downloadAssessmentReport').then(
        ({ downloadAssessmentReport }) => {
          // Pass the full URL directly instead of extracting and reconstructing
          downloadAssessmentReport(reportUrl);
        }
      );
    } else {
      // Direct URL - open in new tab
      window.open(reportUrl, '_blank');
    }
  };

  const renderHeaderActions = () => {
    const availableReports = [];

    // Feedback report - available to all users
    if (feedbackReport) {
      availableReports.push({
        name: 'Feedback Report',
        url: feedbackReport
      });
    }

    // User Summary report - only for recruiters
    if (isRecruiter && userSummaryReport) {
      availableReports.push({
        name: 'User Summary Report',
        url: userSummaryReport
      });
    }

    if (availableReports.length === 0) {
      return null;
    }

    return (
      <Grid container spacing={1} style={{ width: 'auto' }}>
        {availableReports.map((report) => (
          <Grid
            item
            key={report.name}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '8px 12px',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
              minWidth: '80px',
              cursor: 'pointer'
            }}
            onClick={() => handleDownloadReport(report.url, report.name)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <SvgIconStyle
              src="/static/img/icons/downlowdViolet.svg"
              style={{
                width: '24px',
                height: '24px',
                marginBottom: '4px',
                cursor: 'pointer'
              }}
            />
            <Typography
              variant="caption"
              style={{
                fontSize: '11px',
                fontWeight: 500,
                color: '#562C82',
                textAlign: 'center',
                lineHeight: '1.2',
                cursor: 'pointer'
              }}
            >
              {report.name}
            </Typography>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderContent = () => {
    if (shouldShowButton) {
      return (
        <Grid
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            padding: '20px'
          }}
        >
          <Button
            variant="contained"
            onClick={handleTakeTest}
            disabled={initiatingMindgage}
            style={{
              background:
                'linear-gradient(92.25deg, #9B5FDA -2.57%, #562C82 113.38%)',
              padding: '10px 45px',
              borderRadius: '30px',
              textTransform: 'capitalize',
              color: 'white',
              fontWeight: 600
            }}
          >
            {initiatingMindgage ? t('Loading...') : t('Take Mindgage Test')}
          </Button>
        </Grid>
      );
    }

    if (hasResult) {
      // Show loading while fetching metrics
      if (loadingMetrics || (isResultArray && !mindgageMetrics?.length)) {
        return <EmptyContent title={t('Loading Mindgage metrics...')} />;
      }

      // If result is an array of {metric_id, score}, map it to metrics
      if (isResultArray && mindgageMetrics && mindgageMetrics.length > 0) {
        // Create a map of metric_id -> score from result
        const scoreMap = new Map<number, number>();
        resultData.forEach((item: any) => {
          if (item.metric_id && item.score !== undefined) {
            scoreMap.set(item.metric_id, item.score);
          }
        });

        // Metrics structure: Array of {id, name, description}
        // Map metrics to chart format by matching metric_id (from result) with id (from metrics)
        const labels: string[] = [];
        const points: number[] = [];
        const descriptions: any[] = [];

        mindgageMetrics.forEach((metric: any) => {
          if (metric.id && metric.name) {
            const score = scoreMap.get(metric.id);
            if (score !== undefined) {
              labels.push(metric.name);
              points.push(score);
              descriptions.push({
                id: metric.id,
                name: metric.name,
                description: metric.description || ''
              });
            }
          }
        });

        // If we have valid data, display the chart
        if (labels.length > 0 && points.length > 0) {
          return (
            <Grid
              style={{
                maxWidth: '650px',
                margin: '0 auto',
                padding: '20px',
                width: '100%'
              }}
            >
              <Card className="open-job-posts-card">
                <CultureProfileCharts
                  isMindgage={true}
                  descriptions={descriptions}
                  labels={labels}
                  points={points}
                  profile={true}
                  showTooltips={false}
                  minScale={1}
                  maxScale={5}
                  mindgageScoresTooltipIsOpen={mindgageScoresTooltipIsOpen}
                  setMindgageScoresTooltipIsOpen={
                    setMindgageScoresTooltipIsOpen
                  }
                />
              </Card>
            </Grid>
          );
        }
      }

      // Fallback: Use metrics directly if result format is different
      if (mindgageMetrics && mindgageMetrics.length > 0) {
        return (
          <Grid
            style={{
              maxWidth: '650px',
              margin: '0 auto',
              padding: '20px',
              width: '100%'
            }}
          >
            <Card className="open-job-posts-card">
              {mindgageMetrics.map((metricGroup: any, index: number) => (
                <div key={index} style={{ marginBottom: '20px' }}>
                  {metricGroup.labels && metricGroup.labels.length > 0 && (
                    <CultureProfileCharts
                      descriptions={metricGroup.descriptions || []}
                      labels={metricGroup.labels || metricGroup.lables || []}
                      points={metricGroup.data || metricGroup.points || []}
                      profile={true}
                    />
                  )}
                </div>
              ))}
            </Card>
          </Grid>
        );
      }

      return <EmptyContent title={t('No Mindgage metrics available')} />;
    }

    return <EmptyContent title={t('No Mindgage results available')} />;
  };

  return (
    <CandidateCardContainer
      title={title}
      iconSrc={iconSrc}
      headerActions={renderHeaderActions()}
    >
      {renderContent()}
    </CandidateCardContainer>
  );
};
